// BracePlan2D.tsx — 2D plan view for brace plan: slab outline, wall positions,
// brace anchor points (green=ok, red=clash), and brace lines.
// Coordinate system: no Y-flip — plan coords = SVG coords (wx=x, wy=z).

import { useRef, useState, useEffect, useMemo } from 'react';
import type { WallSegment } from './SiteViewport3D';
import type { BraceGeometry } from '../utils/computeBraces';
import type { PanelData } from '../types/panelvar';

interface Props {
  segments:       WallSegment[];
  slabVertices:   [number, number][] | undefined;
  braces:         BraceGeometry[];
  allPanels:      PanelData[];
  checkedPanelIds: Set<string>;
  jointWidth:     number;
}

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 20;

export default function BracePlan2D({ segments, slabVertices, braces, checkedPanelIds }: Props) {
  const [zoom, setZoom] = useState(1);
  const [pan,  setPan]  = useState({ x: 0, y: 0 });
  const svgRef      = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(zoom);
  const panRef  = useRef(pan);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current  = pan;  }, [pan]);
  const vbRef = useRef({ vbX: 0, vbY: 0, vbW: 600, vbH: 400 });

  // World bounding box
  const worldBB = useMemo(() => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    const pts: [number, number][] = slabVertices ?? [];
    for (const seg of segments) {
      pts.push([seg.x, seg.z]);
    }
    for (const b of braces) {
      pts.push([b.anchorX, b.anchorZ]);
    }
    for (const [x, y] of pts) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    if (!Number.isFinite(minX)) return { minX: -720, maxX: 720, minY: -360, maxY: 360 };
    const margin = 120;
    return { minX: minX - margin, maxX: maxX + margin, minY: minY - margin, maxY: maxY + margin };
  }, [slabVertices, segments, braces]);

  // Zoom/pan via wheel and drag
  useEffect(() => {
    const el = svgRef.current;
    const container = containerRef.current;
    if (!el || !container) return;
    const ZOOM_FACTOR = 1.15;
    let dragOrigin: { sx: number; sy: number; px: number; py: number } | null = null;

    function svgPoint(clientX: number, clientY: number) {
      const rect = el!.getBoundingClientRect();
      const { vbX, vbY, vbW, vbH } = vbRef.current;
      return { wx: vbX + ((clientX - rect.left) / rect.width) * vbW, wy: vbY + ((clientY - rect.top) / rect.height) * vbH };
    }
    function applyZoom(nz: number, cx: number, cy: number) {
      nz = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, nz));
      const { wx, wy } = svgPoint(cx, cy);
      const factor = nz / zoomRef.current;
      setPan({ x: wx - (wx - panRef.current.x) * factor, y: wy - (wy - panRef.current.y) * factor });
      setZoom(nz);
    }
    const onWheel = (e: WheelEvent) => { e.preventDefault(); applyZoom(zoomRef.current * (e.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR), e.clientX, e.clientY); };
    const onPointerDown = (e: PointerEvent) => { if (e.button !== 0) return; dragOrigin = { sx: e.clientX, sy: e.clientY, px: panRef.current.x, py: panRef.current.y }; el!.setPointerCapture(e.pointerId); };
    const onPointerMove = (e: PointerEvent) => { if (!dragOrigin) return; const { vbW, vbH } = vbRef.current; const rect = el!.getBoundingClientRect(); setPan({ x: dragOrigin.px - ((e.clientX - dragOrigin.sx) / rect.width) * vbW, y: dragOrigin.py - ((e.clientY - dragOrigin.sy) / rect.height) * vbH }); };
    const onPointerUp = () => { dragOrigin = null; };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'h' || e.key === 'H' || e.key === 'f' || e.key === 'F') { setZoom(1); setPan({ x: 0, y: 0 }); } };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    container.addEventListener('keydown', onKey);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('keydown', onKey);
    };
  }, []);

  const { vbX, vbY, vbW, vbH } = useMemo(() => {
    const bw = worldBB.maxX - worldBB.minX;
    const bh = worldBB.maxY - worldBB.minY;
    const baseW = bw / zoom;
    const baseH = bh / zoom;
    const cx = (worldBB.minX + worldBB.maxX) / 2 + pan.x;
    const cy = (worldBB.minY + worldBB.maxY) / 2 + pan.y;
    const vbX = cx - baseW / 2;
    const vbY = cy - baseH / 2;
    vbRef.current = { vbX, vbY, vbW: baseW, vbH: baseH };
    return { vbX, vbY, vbW: baseW, vbH: baseH };
  }, [zoom, pan, worldBB]);

  function wx(x: number) { return x; }
  function wy(z: number) { return z; }

  const baseFontSize = Math.max(12, vbW / 60);
  const smallFont    = baseFontSize * 0.65;

  const slabPoints = (slabVertices ?? []).map(([x, z]) => `${wx(x)},${wy(z)}`).join(' ');

  // Anchor groups: each anchor has insert + anchor points + clash status
  const anchorGroups = useMemo(() => {
    return braces
      .filter(b => checkedPanelIds.has(b.panelId))
      .map(b => ({
        id: b.id,
        ix: b.insertX, iz: b.insertZ,
        ax: b.anchorX, az: b.anchorZ,
        isClash: b.isClash,
        panelMark: b.panelMark,
      }));
  }, [braces, checkedPanelIds]);

  return (
    <div ref={containerRef} tabIndex={-1} style={{ width: '100%', height: '100%', outline: 'none', cursor: 'grab', background: '#1a1f2e' }}>
      <svg ref={svgRef} width="100%" height="100%"
        viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
        style={{ display: 'block', touchAction: 'none', userSelect: 'none' }}
      >
        {/* Slab polygon */}
        {slabVertices && slabVertices.length >= 3 && (
          <polygon points={slabPoints} fill="#c8c0b0" stroke="#888" strokeWidth={baseFontSize * 0.3} />
        )}

        {/* Wall segments — thin rectangles showing wall thickness */}
        {segments.map(seg => {
          const cosA = Math.cos(seg.rotationY);
          const sinA = Math.sin(seg.rotationY);
          const totalW = seg.panels.reduce((s, p) => s + p.mpvar.width, 0) + (seg.panels.length - 1) * 0;
          const thick = 7.25; // approximate panel thickness for plan display
          // Wall rect center
          const cx = seg.x + (totalW / 2) * cosA;
          const cz = seg.z - (totalW / 2) * sinA;
          const deg = -seg.rotationY * 180 / Math.PI;
          return (
            <g key={seg.elevId} transform={`translate(${wx(cx)},${wy(cz)}) rotate(${deg})`}>
              <rect x={-totalW / 2} y={-thick / 2} width={totalW} height={thick}
                fill="#8a9ab0" stroke="#6a7a90" strokeWidth={baseFontSize * 0.1} opacity={0.8} />
              <text x={0} y={-thick / 2 - smallFont * 0.3}
                textAnchor="middle" fontSize={smallFont} fill="#ccd" fontWeight="600">
                {seg.name}
              </text>
            </g>
          );
        })}

        {/* Brace lines and anchor points */}
        {anchorGroups.map(b => {
          const color = b.isClash ? '#ee4422' : '#44cc66';
          return (
            <g key={b.id}>
              <line
                x1={wx(b.ix)} y1={wy(b.iz)}
                x2={wx(b.ax)} y2={wy(b.az)}
                stroke={color} strokeWidth={baseFontSize * 0.15} opacity={0.7}
              />
              <circle cx={wx(b.ax)} cy={wy(b.az)} r={baseFontSize * 0.5}
                fill={color} opacity={0.85} />
              <circle cx={wx(b.ix)} cy={wy(b.iz)} r={baseFontSize * 0.3}
                fill={color} opacity={0.6} />
            </g>
          );
        })}

        {/* North arrow — arrowhead UP toward N */}
        <g transform={`translate(${vbX + baseFontSize * 3},${vbY + vbH - baseFontSize * 5})`}>
          <circle cx={0} cy={0} r={baseFontSize * 2} fill="none" stroke="#cc2222" strokeWidth={baseFontSize * 0.15} />
          <polygon points={`0,${-baseFontSize * 1.5} ${-baseFontSize * 0.6},0 ${baseFontSize * 0.6},0`} fill="#cc2222" />
          <line x1={0} y1={0} x2={0} y2={baseFontSize * 0.9} stroke="#cc2222" strokeWidth={baseFontSize * 0.2} />
          <text x={0} y={-baseFontSize * 2.4} textAnchor="middle" fontSize={smallFont * 0.9} fill="#cc2222" fontWeight="700">N</text>
        </g>
      </svg>

      <div style={{ position: 'absolute', bottom: 8, right: 12, color: '#555', fontSize: 11, pointerEvents: 'none' }}>
        H = fit · scroll = zoom · drag = pan
      </div>
      <div style={{ position: 'absolute', top: 8, right: 12, color: '#666', fontSize: 11, pointerEvents: 'none' }}>
        <span style={{ color: '#44cc66' }}>●</span> OK &nbsp;
        <span style={{ color: '#ee4422' }}>●</span> Clash
      </div>
    </div>
  );
}
